import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { CloudIcon } from '@heroicons/react/24/solid';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-dark-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <NavLink to="/" className="flex items-center">
                <CloudIcon className="h-8 w-8 text-primary-500" />
                <span className="ml-2 text-white text-xl font-bold">Fraser FJ</span>
              </NavLink>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <NavLink to="/" className={({isActive}) => isActive ? 'nav-link-active' : 'nav-link'}>
                  Home
                </NavLink>
                <NavLink to="/articles" className={({isActive}) => isActive ? 'nav-link-active' : 'nav-link'}>
                  Articles
                </NavLink>
                <NavLink to="/websites" className={({isActive}) => isActive ? 'nav-link-active' : 'nav-link'}>
                  Websites
                </NavLink>
                <NavLink to="/projects" className={({isActive}) => isActive ? 'nav-link-active' : 'nav-link'}>
                  Side Projects
                </NavLink>
                <NavLink to="/contact" className={({isActive}) => isActive ? 'nav-link-active' : 'nav-link'}>
                  Contact
                </NavLink>
              </div>
            </div>
          </div>
          <div className="hidden md:block">
            <a 
              href="https://github.com/Frazzlem8" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-gray-700 bg-[#24292F] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#111827] focus:outline-none focus:ring-2 focus:ring-gray-500/60"
            >
              <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12.02c0 4.42 2.87 8.17 6.84 9.5.5.09.68-.22.68-.48 0-.24-.01-1.03-.01-1.87-2.51.46-3.16-.61-3.36-1.17-.11-.29-.6-1.17-1.03-1.41-.35-.19-.85-.66-.01-.67.79-.01 1.35.73 1.54 1.03.9 1.51 2.33 1.08 2.9.82.09-.65.35-1.08.64-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.03A9.56 9.56 0 0 1 12 6.84c.85 0 1.7.11 2.5.34 1.91-1.3 2.75-1.03 2.75-1.03.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.69 0 3.84-2.34 4.69-4.57 4.94.36.31.68.91.68 1.85 0 1.33-.01 2.41-.01 2.74 0 .27.18.58.69.48A10.02 10.02 0 0 0 22 12.02C22 6.48 17.52 2 12 2z" clipRule="evenodd" />
              </svg>
              GitHub
            </a>
          </div>
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-dark-700 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      <Transition
        show={isOpen}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-dark-800 shadow-lg">
            <NavLink to="/" className={({isActive}) => 
              `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-700 hover:text-white'}`
            }>
              Home
            </NavLink>
            <NavLink to="/articles" className={({isActive}) => 
              `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-700 hover:text-white'}`
            }>
              Articles
            </NavLink>
            <NavLink to="/websites" className={({isActive}) => 
              `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-700 hover:text-white'}`
            }>
              Websites
            </NavLink>
            <NavLink to="/projects" className={({isActive}) => 
              `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-700 hover:text-white'}`
            }>
              Side Projects
            </NavLink>
            <NavLink to="/contact" className={({isActive}) => 
              `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-700 hover:text-white'}`
            }>
              Contact
            </NavLink>
            <a 
              href="https://github.com/Frazzlem8" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-white bg-[#24292F] hover:bg-[#111827]"
            >
              <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12.02c0 4.42 2.87 8.17 6.84 9.5.5.09.68-.22.68-.48 0-.24-.01-1.03-.01-1.87-2.51.46-3.16-.61-3.36-1.17-.11-.29-.6-1.17-1.03-1.41-.35-.19-.85-.66-.01-.67.79-.01 1.35.73 1.54 1.03.9 1.51 2.33 1.08 2.9.82.09-.65.35-1.08.64-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.03A9.56 9.56 0 0 1 12 6.84c.85 0 1.7.11 2.5.34 1.91-1.3 2.75-1.03 2.75-1.03.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.69 0 3.84-2.34 4.69-4.57 4.94.36.31.68.91.68 1.85 0 1.33-.01 2.41-.01 2.74 0 .27.18.58.69.48A10.02 10.02 0 0 0 22 12.02C22 6.48 17.52 2 12 2z" clipRule="evenodd" />
              </svg>
              GitHub
            </a>
          </div>
        </div>
      </Transition>
    </nav>
  );
};

export default Navbar;
